import {useState} from 'react';
import {trpc} from '@common-web/lib/trpc';
import {Button} from '@common-web/components/ui/button';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Check, Plus, Trash2, Loader2, AlertCircle} from 'lucide-react';

interface StepActionSetupPlansProps {
  onboardingId: string;
  stepId: string;
  clientId: string;
  groupId: string | null;
  onComplete: () => void;
}

export function StepActionSetupPlans({
  onboardingId,
  stepId,
  clientId,
  groupId,
  onComplete,
}: StepActionSetupPlansProps) {
  const [error, setError] = useState<string | null>(null);

  const utils = trpc.useUtils();

  const configurationsQuery = trpc.internal.groupPlan.configuration.list.useQuery(
    {clientId},
    {enabled: !!clientId},
  );

  // Get existing plans for the group (using the VBA groupId from the local group)
  const groupQuery = trpc.internal.onboarding.getOnboarding.useQuery({onboardingId});
  const localGroup = groupQuery.data?.group;

  const localGroupDbQuery = trpc.vba.groups.groupDetails.getDetails.useQuery(
    {groupId: localGroup?.name ?? ''},
    {enabled: false}, // We don't want to query VBA directly, we need the local db group
  );

  // Get the vbaGroupId from the local group record
  const groupPlansQuery = trpc.vba.groups.groupPlans.listGroupPlanDetails.useQuery(
    {groupId: groupId ?? ''},
    {enabled: !!groupId},
  );

  const createPlanMutation = trpc.vba.groups.groupPlans.createGroupPlanDetail.useMutation({
    onSuccess: () => {
      if (groupId) {
        utils.vba.groups.groupPlans.listGroupPlanDetails.invalidate({groupId});
      }
      setError(null);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const deletePlanMutation = trpc.vba.groups.groupPlans.deleteGroupPlanDetail.useMutation({
    onSuccess: () => {
      if (groupId) {
        utils.vba.groups.groupPlans.listGroupPlanDetails.invalidate({groupId});
      }
    },
  });

  const completeStepMutation = trpc.internal.onboarding.completeStep.useMutation({
    onSuccess: () => {
      utils.internal.onboarding.getOnboarding.invalidate({onboardingId});
      onComplete();
    },
  });

  function handleAddPlan(variationId: string) {
    if (!groupId) return;
    createPlanMutation.mutate({
      groupId,
      groupPlanConfigurationVariationId: variationId,
    });
  }

  function handleRemovePlan(planDetailId: string) {
    deletePlanMutation.mutate({planDetailId});
  }

  function handleMarkComplete() {
    completeStepMutation.mutate({stepId});
  }

  if (!groupId) {
    return (
      <div className='rounded-md bg-amber-50 p-3'>
        <p className='text-xs text-amber-700'>
          A group must be created first before plans can be assigned. Complete the "Create Group in
          VBA" step first.
        </p>
      </div>
    );
  }

  const configurations = configurationsQuery.data?.configurations ?? [];
  const assignedPlans = groupPlansQuery.data?.plans ?? [];
  const assignedVariationIds = new Set(
    assignedPlans.map(
      (p: {groupPlanConfigurationVariationId: string}) => p.groupPlanConfigurationVariationId,
    ),
  );

  return (
    <div className='space-y-4'>
      <div className='rounded-md bg-blue-50 p-3'>
        <p className='text-xs text-blue-700'>
          Select plan configurations to assign to this group. Plans available are based on the
          client's configured plan templates.
        </p>
      </div>

      {/* Currently assigned plans */}
      {assignedPlans.length > 0 && (
        <div className='space-y-2'>
          <p className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
            Assigned Plans
          </p>
          {assignedPlans.map(
            (plan: {
              id: string;
              groupPlanConfigurationVariation: {
                id: string;
                name: string;
                groupPlanConfiguration: {name: string};
              };
            }) => (
              <Card key={plan.id}>
                <CardContent className='p-3 flex items-center justify-between'>
                  <div>
                    <p className='text-sm font-medium text-gray-900'>
                      {plan.groupPlanConfigurationVariation.groupPlanConfiguration.name}
                    </p>
                    <p className='text-xs text-gray-500'>
                      {plan.groupPlanConfigurationVariation.name}
                    </p>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Badge className='bg-emerald-100 text-emerald-700 text-xs'>
                      <Check className='h-3 w-3 mr-1' />
                      Assigned
                    </Badge>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='h-7 w-7'
                      onClick={() => handleRemovePlan(plan.id)}
                      disabled={deletePlanMutation.isPending}
                    >
                      <Trash2 className='h-3.5 w-3.5 text-red-500' />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ),
          )}
        </div>
      )}

      {/* Available plans to add */}
      <div className='space-y-2'>
        <p className='text-xs font-medium text-gray-500 uppercase tracking-wider'>
          Available Plans
        </p>
        {configurationsQuery.isLoading ? (
          <div className='text-center py-4'>
            <Loader2 className='h-4 w-4 animate-spin mx-auto text-gray-400' />
          </div>
        ) : configurations.length === 0 ? (
          <p className='text-xs text-gray-400 text-center py-4'>
            No plan configurations found for this client.
          </p>
        ) : (
          configurations.map(
            (config: {
              id: string;
              name: string;
              description: string;
              variations: Array<{
                id: string;
                name: string;
                maxPayoutPEPM: unknown;
              }>;
            }) => (
              <Card key={config.id}>
                <CardContent className='p-3'>
                  <p className='text-sm font-medium text-gray-900'>{config.name}</p>
                  <p className='text-xs text-gray-500 mb-2'>{config.description}</p>
                  <div className='space-y-1'>
                    {config.variations.map((variation) => {
                      const isAssigned = assignedVariationIds.has(variation.id);
                      return (
                        <div
                          key={variation.id}
                          className='flex items-center justify-between py-1.5 px-2 rounded-sm bg-gray-50'
                        >
                          <div>
                            <span className='text-xs font-medium text-gray-700'>
                              {variation.name}
                            </span>
                            {variation.maxPayoutPEPM != null ? (
                              <span className='text-xs text-gray-400 ml-2'>
                                {'$'}
                                {String(variation.maxPayoutPEPM)} PEPM
                              </span>
                            ) : null}
                          </div>
                          {isAssigned ? (
                            <Badge variant='outline' className='text-xs text-emerald-600'>
                              Added
                            </Badge>
                          ) : (
                            <Button
                              variant='ghost'
                              size='sm'
                              className='h-6 text-xs'
                              onClick={() => handleAddPlan(variation.id)}
                              disabled={createPlanMutation.isPending}
                            >
                              <Plus className='h-3 w-3 mr-1' />
                              Add
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ),
          )
        )}
      </div>

      {error && (
        <div className='rounded-md bg-red-50 p-3 flex items-start gap-2'>
          <AlertCircle className='h-4 w-4 text-red-600 shrink-0 mt-0.5' />
          <p className='text-xs text-red-700'>{error}</p>
        </div>
      )}

      {assignedPlans.length > 0 && (
        <Button
          className='w-full bg-emerald-600 hover:bg-emerald-700 text-white'
          onClick={handleMarkComplete}
          disabled={completeStepMutation.isPending}
        >
          {completeStepMutation.isPending ? 'Completing...' : 'Mark Plans Setup Complete'}
        </Button>
      )}
    </div>
  );
}
